import { useState } from "react";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail, useServices } from "../lib/api";

const budgets = ["Under $50K", "$50K – $250K", "$250K – $1M", "$1M – $5M", "$5M+", "Not sure yet"];
const timelines = ["ASAP / Emergency", "Within 3 months", "3–6 months", "6–12 months", "Planning phase"];
const projectTypes = ["Government / Federal", "Commercial", "Residential", "Multi-Family", "Municipal", "Other"];

export default function QuotePage() {
  const { data: services = [] } = useServices();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: "",
    project_type: "",
    budget: "",
    timeline: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.service_interest) {
      toast.error("Please select a service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/forms/quote", form);
      toast.success("Quote request received. We'll follow up within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", service_interest: "", project_type: "", budget: "", timeline: "", details: "" });
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHero
        overline="Request a Quote"
        title="Scope it. Price it. Build it."
        subtitle="Give us the essentials and our estimating team will respond with next steps within one business day."
      />

      <section className="bg-canvas py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="border border-line bg-panel p-7 md:p-10 space-y-6" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="q-name">Full Name *</Label>
                <Input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-canvas" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-email">Email *</Label>
                <Input id="q-email" data-testid="quote-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-canvas" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-phone">Phone</Label>
                <Input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none bg-canvas" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-company">Company / Agency</Label>
                <Input id="q-company" data-testid="quote-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none bg-canvas" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label>Service Needed *</Label>
                <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                  <SelectTrigger data-testid="quote-service-select" className="rounded-none bg-canvas">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Project Type</Label>
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger data-testid="quote-type-select" className="rounded-none bg-canvas">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Estimated Budget</Label>
                <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                  <SelectTrigger data-testid="quote-budget-select" className="rounded-none bg-canvas">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {budgets.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Timeline</Label>
                <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
                  <SelectTrigger data-testid="quote-timeline-select" className="rounded-none bg-canvas">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    {timelines.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="q-details">Project Details *</Label>
              <Textarea
                id="q-details"
                data-testid="quote-details-input"
                required
                rows={6}
                placeholder="Location, scope, square footage, current condition — anything that helps us estimate accurately."
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                className="rounded-none bg-canvas"
              />
            </div>

            <Button type="submit" data-testid="quote-submit-button" disabled={submitting} className="w-full rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <CircleNotch size={16} className="animate-spin" /> Submitting…
                </span>
              ) : (
                "Submit Quote Request"
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
